import React from "react";
import { Box, Card, CardContent, CardHeader, Typography } from "@mui/material";
import PropTypes from "prop-types";
import AnalyticsHeader from "visual-components/analyticsDashboard/analytics-header";
import CardDateRangePickerRow from "visual-components/util/cards/card-date-range-picker-row";
import GraphEmptyState from "visual-components/util/graphs/empty-state";
import GeoDistributionBar from "visual-components/util/graphs/geo-distribution-bar";

/**
 * Geographic Distribution Card Component
 * Displays top regions for email acquisition
 * @param {Object} props
 * @param {string} [props.name] - Optional name identifier
 * @param {Array} props.els - Array of geographic data elements
 * @param {Date} props.oldestDate - Oldest date for date picker
 * @param {boolean} [props.alignLeft=false] - Align date picker left
 * @param {Object} props.datePicker - Date picker configuration
 * @param {Function} props.changeFilter - Filter change handler
 * @returns {import("react").ReactElement}
 */
export default function GeoDistributionCard(props) {
  const { name, els, oldestDate, alignLeft = false, datePicker, changeFilter } = props;

  const newestDate = new Date();
  const hasData = els && els.some((el) => el.perc > 0);

  return (
    <Card sx={{ width: 314, height: "auto", overflow: "visible" }}>
      <CardHeader
        title={
          <AnalyticsHeader
            title="Geographic Distribution"
            tooltipText="The top three regions where you have acquired emails"
          />
        }
      />
      <CardContent sx={{ pb: 1 }}>
        {hasData ? (
          <Box>
            <Typography
              variant="caption"
              sx={{ display: "block", pb: 2, fontStyle: "italic", textTransform: "uppercase" }}
            >
              Emails Acquired - Top Regions
            </Typography>
            <Box sx={{ mt: -1, mb: -1, height: 110 }}>
              <GeoDistributionBar values={els} />
            </Box>
          </Box>
        ) : (
          <GraphEmptyState text="You haven't requested geographic data from entrants. Add a zip code to future entry forms to view this data." />
        )}
      </CardContent>
      <CardDateRangePickerRow
        oldestDate={oldestDate}
        newestDate={newestDate}
        changeFilter={changeFilter}
        alignLeft={alignLeft}
        {...datePicker}
      />
    </Card>
  );
}

GeoDistributionCard.propTypes = {
  alignLeft: PropTypes.bool,
  changeFilter: PropTypes.func.isRequired,
  datePicker: PropTypes.object.isRequired,
  els: PropTypes.array.isRequired,
  oldestDate: PropTypes.instanceOf(Date).isRequired,
  name: PropTypes.string,
};

GeoDistributionCard.defaultProps = {
  alignLeft: false,
  name: undefined,
};
